/**
 * Recording a debrief (DEBRIEF-RECORDING) — the laptop half.
 *
 * The edge appliance's cameras record the picture; this records the sound
 * (the cameras have none) and uploads it in chunks as it goes, so a closed
 * tab or a crash loses seconds, not the conversation. It runs app-wide (a
 * provider in main.tsx) with a small bar instead of a dialog: the operator
 * keeps reviewing the session on this same laptop while it records, and may
 * move around the portal.
 *
 * Timing: the server answers the start with its clock (`server_now`); the
 * laptop's clock offset is estimated from that round trip, and the moment the
 * microphone actually started is sent, on the server's clock, as seconds
 * after the debrief's start. The cameras report theirs the same way, which
 * is how the audio lands on the video.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

import { extractErrorMessage } from '../../api/errors';
import {
  debriefHeartbeat,
  putDebriefAudio,
  startDebrief,
  stopDebrief,
} from '../../api/debriefs';

export const CHUNK_MS = 5000;
export const HEARTBEAT_MS = 20_000;

/** The laptop's clock minus the server's, from one round trip. */
export function clockOffsetMs(sentAt: number, receivedAt: number, serverNowIso: string): number {
  return (sentAt + receivedAt) / 2 - Date.parse(serverNowIso);
}

/** When the microphone started, in seconds after the debrief started — on
 *  the server's clock. */
export function audioOffsetSeconds(
  micStartedAt: number,
  offsetMs: number,
  debriefStartedIso: string,
): number {
  return (micStartedAt - offsetMs - Date.parse(debriefStartedIso)) / 1000;
}

/** A recorder format the browser can make and the server accepts. */
export function pickMime(isSupported: (m: string) => boolean): string | null {
  for (const m of ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']) {
    if (isSupported(m)) return m;
  }
  return null;
}

export interface DebriefStartOptions {
  tenantId: string;
  sessionRowId: string;
  applianceId: string;
  cameraIds: string[];
  /** Shown in the bar, e.g. the participant's name. */
  label: string;
  /** After the debrief has been handed over for assembly. */
  onStopped?: () => void;
}

interface DebriefRecorderApi {
  active: { label: string; sessionRowId: string } | null;
  start: (o: DebriefStartOptions) => Promise<void>;
  stop: () => Promise<void>;
}

const Ctx = createContext<DebriefRecorderApi | null>(null);

/** The recorder, or null outside DebriefRecorderProvider (the button then
 *  doesn't show). */
export function useDebriefRecorder(): DebriefRecorderApi | null {
  return useContext(Ctx);
}

interface Running {
  tenantId: string;
  debriefId: string;
  sessionRowId: string;
  label: string;
  startedIso: string;
  maxSeconds: number;
  offsetMs: number;
  micStartedAt: number | null;
  mime: string;
  stream: MediaStream;
  recorder: MediaRecorder;
  seq: number;
  queue: { seq: number; blob: Blob }[];
  onStopped?: () => void;
}

export function DebriefRecorderProvider({ children }: { children: ReactNode }) {
  const run = useRef<Running | null>(null);
  const [active, setActive] = useState<DebriefRecorderApi['active']>(null);
  const [elapsed, setElapsed] = useState(0);
  const [level, setLevel] = useState(0);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const uploading = useRef(false);

  // Upload queued chunks in order, retrying; a chunk stays queued until the
  // server has it.
  const drain = useCallback(async () => {
    const r = run.current;
    if (!r || uploading.current) return;
    uploading.current = true;
    try {
      while (r.queue.length > 0) {
        const { seq, blob } = r.queue[0];
        let ok = false;
        for (const wait of [0, 2000, 5000]) {
          if (wait) await new Promise((res) => setTimeout(res, wait));
          try {
            await putDebriefAudio(r.tenantId, r.debriefId, seq, blob, r.mime);
            ok = true;
            break;
          } catch {
            /* retry */
          }
        }
        if (!ok) break; // try again on the next chunk / at stop
        r.queue.shift();
      }
    } finally {
      uploading.current = false;
    }
  }, []);

  const finish = useCallback(async () => {
    const r = run.current;
    if (!r) return;
    setSaving(true);
    await new Promise<void>((resolve) => {
      if (r.recorder.state === 'inactive') {
        resolve();
        return;
      }
      r.recorder.addEventListener('stop', () => resolve(), { once: true });
      r.recorder.stop();
    });
    r.stream.getTracks().forEach((tr) => tr.stop());
    const deadline = Date.now() + 60_000;
    while (r.queue.length > 0 && Date.now() < deadline) {
      await drain();
      if (r.queue.length > 0) await new Promise((res) => setTimeout(res, 1000));
    }
    try {
      await stopDebrief(r.tenantId, r.debriefId, {
        audio_offset_s:
          r.micStartedAt === null ? null : audioOffsetSeconds(r.micStartedAt, r.offsetMs, r.startedIso),
        audio_parts: r.seq,
        audio_mime: r.mime.split(';')[0],
      });
      setNotice(
        r.queue.length > 0
          ? 'Debrief stopped — some audio could not be uploaded.'
          : 'Debrief saved. It appears under Transcript once it has been put together.',
      );
    } catch (e) {
      setError(extractErrorMessage(e, 'could not stop the debrief'));
    }
    run.current = null;
    setActive(null);
    setSaving(false);
    r.onStopped?.();
  }, [drain]);

  const start = useCallback(
    async (o: DebriefStartOptions) => {
      if (run.current) throw new Error('a debrief is already being recorded');
      const mime = pickMime((m) =>
        typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m),
      );
      if (!mime) throw new Error('this browser can’t record audio');
      // The microphone first: the permission prompt must not delay the audio
      // after the cameras have started.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      const sentAt = Date.now();
      let started;
      try {
        started = await startDebrief(o.tenantId, o.sessionRowId, {
          appliance_id: o.applianceId,
          camera_ids: o.cameraIds,
        });
      } catch (e) {
        stream.getTracks().forEach((tr) => tr.stop());
        throw e;
      }
      const offsetMs = clockOffsetMs(sentAt, Date.now(), started.server_now);
      const recorder = new MediaRecorder(stream, { mimeType: mime });
      const r: Running = {
        tenantId: o.tenantId, debriefId: started.id, sessionRowId: o.sessionRowId,
        label: o.label, startedIso: started.started_at, maxSeconds: started.max_seconds,
        offsetMs, micStartedAt: null, mime, stream, recorder, seq: 0, queue: [],
        onStopped: o.onStopped,
      };
      recorder.addEventListener('start', () => {
        r.micStartedAt = Date.now();
      });
      recorder.addEventListener('dataavailable', (e: BlobEvent) => {
        if (!e.data || e.data.size === 0) return;
        r.queue.push({ seq: r.seq, blob: e.data });
        r.seq += 1;
        void drain();
      });
      run.current = r;
      recorder.start(CHUNK_MS);
      setElapsed(0);
      setError(null);
      setActive({ label: o.label, sessionRowId: o.sessionRowId });
    },
    [drain],
  );

  const stop = useCallback(() => finish(), [finish]);

  // Clock, heartbeat, time limit and the mic level, while recording.
  useEffect(() => {
    const r = run.current;
    if (!active || !r) return;
    const tick = window.setInterval(() => {
      const s = (Date.now() - Date.parse(r.startedIso)) / 1000;
      setElapsed(s);
      if (s >= r.maxSeconds) void finish();
    }, 500);
    const beat = window.setInterval(() => {
      void debriefHeartbeat(r.tenantId, r.debriefId)
        .then((still) => {
          if (!still) void finish();
        })
        .catch(() => undefined);
    }, HEARTBEAT_MS);
    let raf = 0;
    let ctx: AudioContext | null = null;
    try {
      ctx = new AudioContext();
      const analyser = ctx.createAnalyser();
      ctx.createMediaStreamSource(r.stream).connect(analyser);
      const buf = new Uint8Array(analyser.fftSize);
      const loop = () => {
        analyser.getByteTimeDomainData(buf);
        let peak = 0;
        for (const v of buf) peak = Math.max(peak, Math.abs(v - 128));
        setLevel(Math.min(100, (peak / 128) * 160));
        raf = requestAnimationFrame(loop);
      };
      loop();
    } catch {
      /* no level meter — recording still works */
    }
    const leave = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', leave);
    return () => {
      window.clearInterval(tick);
      window.clearInterval(beat);
      cancelAnimationFrame(raf);
      void ctx?.close();
      window.removeEventListener('beforeunload', leave);
    };
  }, [active, finish]);

  const api = useMemo(() => ({ active, start, stop }), [active, start, stop]);
  const mm = Math.floor(elapsed / 60);
  const ss = Math.floor(elapsed % 60).toString().padStart(2, '0');

  return (
    <Ctx.Provider value={api}>
      {children}
      {active && (
        <Paper
          elevation={8}
          role="status"
          aria-label="Recording a debrief"
          sx={{ position: 'fixed', left: 16, bottom: 16, zIndex: 1400, p: 1.5, minWidth: 280 }}
        >
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <FiberManualRecordIcon color="error" fontSize="small" />
              <Typography variant="subtitle2" sx={{ flex: 1 }}>
                Recording debrief · {active.label}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {mm}:{ss}
              </Typography>
            </Stack>
            <LinearProgress variant="determinate" value={level} aria-label="Microphone level" />
            <Box>
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={() => void stop()}
                disabled={saving}
              >
                {saving ? 'Saving the audio…' : 'Stop debrief'}
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}
      <Snackbar open={!!notice} autoHideDuration={8000} onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>
      </Snackbar>
      <Snackbar open={!!error} onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Ctx.Provider>
  );
}
